import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

const AppearanceSettings: React.FC = () => {
  const { addToast } = useToast();
  const [schoolName, setSchoolName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#0056b3');
  const [secondaryColor, setSecondaryColor] = useState('#ff8c00');
  const [emailSender, setEmailSender] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('settings/');
        const data = response.data;
        setSchoolName(data.school_name);
        setLogoUrl(data.logo_url);
        setPrimaryColor(data.primary_color);
        setSecondaryColor(data.secondary_color);
        setEmailSender(data.email_sender);
        
        // Apply colors to the document
        document.documentElement.style.setProperty('--primary-blue', data.primary_color);
        document.documentElement.style.setProperty('--secondary-orange', data.secondary_color);
      } catch (error) {
        console.error("Error fetching settings", error);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.post('settings/', {
        school_name: schoolName,
        logo_url: logoUrl,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        email_sender: emailSender
      });
      
      // Update variables globally
      document.documentElement.style.setProperty('--primary-blue', primaryColor);
      document.documentElement.style.setProperty('--secondary-orange', secondaryColor);
      
      addToast('Configurações de aparência atualizadas com sucesso!', 'success');
    } catch (error) {
      console.error("Error saving settings", error);
      addToast('Erro ao salvar configurações.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '15px', marginBottom: '25px' }}>
        <h3 style={{ margin: 0 }}>🎨 Identidade e Sistema</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '5px' }}>
          Personalize as cores, marca e comunicações da plataforma.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '25px' }}>
        {/* Informações Básicas */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Nome da Instituição</label>
            <input 
              type="text" 
              value={schoolName} 
              onChange={(e) => setSchoolName(e.target.value)} 
              placeholder="Ex: Colégio Terra do Saber"
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>E-mail Remetente</label>
            <input 
              type="email" 
              value={emailSender} 
              onChange={(e) => setEmailSender(e.target.value)} 
              placeholder="noreply@escola.com.br" 
            />
            <small style={{ color: 'var(--text-light)', marginTop: '4px', display: 'block' }}>
              Usado para recuperação de senha e notificações.
            </small>
          </div>
        </div>

        {/* Branding e Cores */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>URL da Logo</label>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <input 
                type="text" 
                value={logoUrl} 
                onChange={(e) => setLogoUrl(e.target.value)} 
                placeholder="https://link-da-sua-imagem.png"
                style={{ flex: 1 }}
              />
              {logoUrl && (
                <div style={{ 
                  width: '50px', 
                  height: '50px', 
                  borderRadius: '8px', 
                  border: '1px solid var(--border-color)',
                  overflow: 'hidden',
                  background: '#f9f9f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <img src={logoUrl} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Cor Primária</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input 
                  type="color" 
                  value={primaryColor} 
                  onChange={(e) => setPrimaryColor(e.target.value)} 
                  style={{ width: '45px', height: '40px', padding: '2px', cursor: 'pointer' }} 
                />
                <span style={{ fontSize: '0.8rem', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{primaryColor.toUpperCase()}</span>
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Cor Secundária</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input 
                  type="color" 
                  value={secondaryColor} 
                  onChange={(e) => setSecondaryColor(e.target.value)} 
                  style={{ width: '45px', height: '40px', padding: '2px', cursor: 'pointer' }} 
                />
                <span style={{ fontSize: '0.8rem', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{secondaryColor.toUpperCase()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end' }}>
        <button 
          className="btn" 
          onClick={handleSave} 
          disabled={loading}
          style={{ minWidth: '200px' }}
        >
          {loading ? 'Salvando...' : 'Salvar Configurações'}
        </button>
      </div>
    </div>
  );
};

export default AppearanceSettings;
